import React from 'react';
import { accountColumns, getContractColumns, getDepartmentColumn, getPartnerColumns } from '../../cols-definition'; // Adjust the path as needed
import { AccountModel, BoPhan, HopDong, KhachHang } from '@/types/schemas';
import { SearchField, FormField } from '@/components/custom/erp';
import QUERY_KEYS from '@/constants/query-keys';
import { Label } from '@/components/ui/label';

interface GeneralInfoTabProps {
  formMode: 'add' | 'edit' | 'view';
  hopDong: HopDong | null;
  boPhan: BoPhan | null;
  khachHang: KhachHang | null;
  taiKhoan: AccountModel | null;
  setHopDong: (hopDong: HopDong) => void;
  setBoPhan: (boPhan: BoPhan) => void;
  setKhachHang: (khachHang: KhachHang) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
}

const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({
  formMode,
  hopDong,
  boPhan,
  khachHang,
  taiKhoan,
  setHopDong,
  setBoPhan,
  setKhachHang,
  setTaiKhoan
}) => {
  return (
    <div className='h-96 px-6 pt-4'>
      <div className='flex flex-col gap-y-4 md:gap-y-6'>
        <div className='space-y-2 md:space-y-2'>
          {/* Loại khế ước & Ngày */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Loại khế ước</Label>
            <div className='flex items-center'>
              <FormField
                type='select'
                name='loai_ku'
                className='w-[260px]'
                options={[
                  { value: '1', label: '1. Đi vay' },
                  { value: '2', label: '2. Cho vay' }
                ]}
              />
              <Label className='ml-8 w-20 min-w-20'>Ngày</Label>
              <FormField type='date' name='ngay_ku' className='w-[180px]' />
            </div>
          </div>

          {/* Ngày vay & Ngày đáo hạn */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Ngày vay</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <FormField type='date' name='ngay_ku1' className='w-[180px]' />
              </div>
              <Label className='ml-8 w-20 min-w-20'>Ngày đáo hạn</Label>
              <FormField type='date' name='ngay_ku2' className='w-[180px]' />
            </div>
          </div>

          {/* Tiền vay nt & Tiền vay */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Tiền vay nt</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <FormField type='number' name='tien_nt' className='w-[180px]' />
              </div>
              <Label className='ml-8 w-20 min-w-20'>Tiền vay</Label>
              <FormField type='number' name='tien_nt' className='w-[180px]' />
            </div>
          </div>

          {/* Hợp đồng */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Hợp đồng</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <SearchField<HopDong>
                  type='text'
                  displayRelatedField={'ten_hd'}
                  columnDisplay={'ma_hd'}
                  className='w-[180px]'
                  searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
                  searchColumns={getContractColumns}
                  dialogTitle='Danh mục hợp đồng'
                  value={hopDong?.ma_hd || ''}
                  onRowSelection={setHopDong}
                  disabled={formMode === 'view'}
                />
              </div>
            </div>
          </div>

          {/* Bộ phận */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Bộ phận</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <SearchField<BoPhan>
                  type='text'
                  displayRelatedField={'ten_bp'}
                  columnDisplay={'ma_bp'}
                  className='w-[180px]'
                  searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
                  searchColumns={getDepartmentColumn}
                  dialogTitle='Danh mục bộ phận'
                  value={boPhan?.ma_bp || ''}
                  onRowSelection={setBoPhan}
                  disabled={formMode === 'view'}
                />
              </div>
            </div>
          </div>
          {/* Khách hàng */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Khách hàng</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <SearchField<KhachHang>
                  type='text'
                  displayRelatedField={'customer_name'}
                  columnDisplay={'customer_code'}
                  className='w-[180px]'
                  searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
                  searchColumns={getPartnerColumns}
                  dialogTitle='Danh mục đối tượng'
                  value={khachHang?.customer_code || ''}
                  onRowSelection={setKhachHang}
                  disabled={formMode === 'view'}
                />
              </div>
            </div>
          </div>
          {/* Tài khoản */}
          <div className='flex items-center'>
            <Label className='w-32 min-w-32'>Tài khoản</Label>
            <div className='flex items-center'>
              <div className='w-[260px]'>
                <SearchField<AccountModel>
                  type='text'
                  displayRelatedField={'name'}
                  columnDisplay={'code'}
                  className='w-[180px]'
                  searchEndpoint={`/accounts/`}
                  searchColumns={accountColumns}
                  dialogTitle='Danh mục tài khoản'
                  value={taiKhoan?.code || ''}
                  onRowSelection={setTaiKhoan}
                  disabled={formMode === 'view'}
                />
              </div>
            </div>
          </div>

          {/* Tình trạng */}
          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 w-32 min-w-32 sm:mb-0'>Tình trạng</Label>
            <FormField
              type='select'
              name='tinh_trang'
              className='w-64'
              options={[
                { value: '0', label: '0 - Chưa thực hiện' },
                { value: '1', label: '1 - Đang thực hiện' },
                { value: '2', label: '2 - Hoàn thành' },
                { value: '3', label: '3 - Tạm dừng' },
                { value: '4', label: '4 - Hủy' }
              ]}
            />
          </div>

          {/* Trạng thái */}
          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 w-32 min-w-32 sm:mb-0'>Trạng thái</Label>
            <FormField
              type='select'
              name='status'
              className='w-64'
              options={[
                { value: '1', label: '1. Còn sử dụng' },
                { value: '0', label: '0. Không sử dụng' }
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInfoTab;
